import React, {useState, useEffect, useContext, useRef} from 'react';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { Layout } from './components/index';
import * as Features from './features/index';
import RedirectHandler from './features/RedirectHandler';
import PrivateRoute from './utilites/private-route';
import { AuthContext, HasRight, Rights } from './utilites/auth';
import { AuthApi } from './apis/auth-api';
import { UserApi } from './apis/user-api';
import { useCookies } from 'react-cookie';
import ThemeSelector from './utilites/themes/ThemeSelector';
import './styles/App.scss';
import IdleTimer from 'react-idle-timer';
import IdleTimeOutModal from './components/modals/IdleTimeOutModal';
import ScrollButton from './components/ScrollToTopButton';
import { Logger } from './utilites/loglySetup';
import ErrorBoundary from './features/ErrorBoundary';
import 'react-perfect-scrollbar/dist/css/styles.css';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Button, notification } from 'antd';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import axios, { AxiosError } from 'axios';
import CloseableErrorMessage from './components/CloseableErrorMessage';

/**
 * Timeout in milliseconds for idle timer
 * Default to 1 hour
 */
const TIMEOUT = process.env.REACT_APP_IDLE_TIMEOUT;

interface GlobalUserProps {
    userId: number;
    userName: string;
    rights: string[];
    sessionTimeout: number;
}

function App() {
    const { setLoggedIn, setProfile, profile, mainScrollbarScrollTop } = useContext(AuthContext);
    const [isLoaded, setIsLoaded] = useState(false);
    const [cookies, setCookie, removeCookie] = useCookies(['sessionUserName']);
    const [isDownloadFile, setIsDownloadFile] = useState<boolean>(false);
    const [isContributor, setIsContributor] = useState<boolean>(false);
    const history = useHistory();
    const [isTimeoutModalVisible, setIsTimeoutModalVisible] = useState<boolean>(false);
    const idleTimerRef = useRef<IdleTimer>(null);
    const [reloadOnPathChange, setReloadOnPathChange] = useState<boolean>(false);
    const location = useLocation();
    const psRef = useRef<PerfectScrollbar>(null);

    useEffect(() => {
        if(!location.pathname.includes("resetpassword")) {
          const t = window.location.pathname.split('/')[1] === 'contributor';
          const isContributor = window.location.pathname.split('/')[1] === 'contributor';
          setIsContributor(isContributor)

          const checkSession = async () => {
              const isRedirect = window.location.pathname.split('/')[1].toLowerCase() === 'redirect';
              if (cookies.sessionUserName !== undefined && !isRedirect) {
                  try {
                      const isDownload = window.location.pathname.split('/')[3] === 'downloadFile';
                      setIsDownloadFile(isDownload);
                      const userProfile = await AuthApi.getUserProfile();
                      if (!userProfile) {
                          history.push('/');
                          setIsLoaded(true);
                          return;
                      }

                      //Currently this call only gets rights, but may change to get more info
                      const rights = await UserApi.getCSUserProfile();

                      const profile: GlobalUserProps = {
                          userId: userProfile.userId,
                          rights: rights,
                          userName: userProfile.userName,
                      }

                      setLoggedIn(true);
                      localStorage.setItem('currentUser', JSON.stringify({ userId: profile.userId }));
                      setProfile(profile);
                      setCookie(`lastaccessed_csportal${profile?.userId}`, {path: "/"});
                      setIsLoaded(true);
                  } catch (e) {
                    if ((e as any).isAxiosError) {
                        if ((e as AxiosError).response?.status === 401) {
                            //This is probably orphaned from the access token/cookie -- get rid of it
                            removeCookie('sessionUserName', { path: "/" });
                        }
                    }
                      Logger().error(e);
                      history.push('/');
                      setIsLoaded(true);
                      return;
                  }
              } else {
                  if(cookies.sessionUserName !== undefined) {
                    setLoggedIn(true);
                  }
                  setIsLoaded(true);
              }
          }

          checkSession();

          window.addEventListener('focus', checkFocusIdleTime);
        } else {
          setIsLoaded(true);
        }

        //App updater/service-worker
        serviceWorkerRegistration.register({
            onUpdate: registration => {
                const waitingServiceWorker = registration.waiting;

                if (waitingServiceWorker) {
                    waitingServiceWorker.addEventListener('statechange', event => {
                        if (event.target?.state === 'activated') {
                            if (window.location.pathname === '/' || window.location.pathname === '/login') {
                                //If at login screen automatically update and refresh on next navigation
                                setReloadOnPathChange(true);
                            }
                            else {
                                openUpdateNotification();
                            }
                        }
                    });
                    waitingServiceWorker.postMessage({ type: "SKIP_WAITING" })
                }
            }
        });
      }, []);

    useEffect(() => {
        if (reloadOnPathChange)
            window.location.reload();
    }, [location, reloadOnPathChange]);

    let isAdmin: boolean = HasRight([Rights.CSAdmin]);

    const LayoutRoutes = (
        <Layout
            psRef={psRef}
        >
          <PrivateRoute path="/" component={Features.Login} />
          <PrivateRoute path="/admin/dashboard" component={Features.Dashboard} />
          <PrivateRoute path="/admin/search" component={Features.Search} />
          <PrivateRoute path="/admin/company" component={Features.CompanyDetail} />
          <PrivateRoute path="/admin/logs" component={isAdmin ? Features.Logs : Features.Dashboard} />
          <PrivateRoute path="/admin/products" component={isAdmin ? Features.Products : Features.Dashboard} />
          <PrivateRoute path="/admin/users/export" component={Features.UserExport} />
          <PrivateRoute path="/admin/users/manage" component={isAdmin ? Features.ManageUsers : Features.Dashboard} />
          <PrivateRoute path="/admin/user" component={Features.UserDetail} />
          <PrivateRoute path="/admin/manage/indexing" component={Features.ManageIndexing} />
          <PrivateRoute path="/admin/meetings" component={Features.Meetings} />
          <PrivateRoute path="/admin/gabii" component={Features.GabiiReport} />
          <PrivateRoute path="/admin/skynet" component={Features.Skynet} />
          <PrivateRoute path="/admin/defaultUser" component={isAdmin ? Features.DefaultUser : Features.Dashboard} />
          <PrivateRoute path="/redirect/index" component={RedirectHandler} />
          <PrivateRoute path="/admin/companyDeletionManagement" component={Features.CompanyDeletionManagement} />
        </Layout>
    )

    /**
     * Show timeout modal when user is idle
     */
    const onIdle = () => {
        const timeDifference = (profile?.sessionTimeout + idleTimerRef?.current?.state?.lastActive) - new Date().getTime();
        if (timeDifference < -30000) {
            signOut(true);
        } else {
            setIsTimeoutModalVisible(true);
        }
    }

    const checkFocusIdleTime = () => {
        const timeDifference = (profile?.sessionTimeout + idleTimerRef?.current?.state?.lastActive) - new Date().getTime();
        if (timeDifference < -30000) {
            signOut(true);
        }
    }

    /**
     * sign out
     * @param forced true if user clicks on Sign Out Now button in time out modal
     */
    const signOut = async (forced: boolean = false) => {
        try{
            await AuthApi.signOut();
            setLoggedIn(false);
            removeCookie('sessionUserName', { path: "/" });
            if (forced) setCookie('forcedSignOut', true, { path: "/"});
            localStorage.removeItem('currentUser');
            localStorage.removeItem('downloadFileId');

            setIsTimeoutModalVisible(false);
        }
        catch (error) {
            if (axios.isCancel(error)) 
                Logger().log(error);
            else 
                Logger().error(error);
            CloseableErrorMessage("Error signing out.");
        }  

        
    }

    const openUpdateNotification = (): void => {
        const updaterNotificationKey = 'updater_notification';

        const updateAppBtn = (
            <Button type="primary" size="small" onClick={() => window.location.reload()}>
                Update
            </Button>
        );

        notification.info({
            message: 'Update Available',
            description:
                'A new version of this app is available. Press "Update" to update (and lose any unsaved changes) or "X" to continue working.',
            key: updaterNotificationKey,
            btn: updateAppBtn,
            placement: 'bottomLeft',
            duration: null,
            onClose: () => {
                //start a "nagging timer" so the user can't indefinitely avoid the update. (Hardcoded to 20 minutes)
                setTimeout(openUpdateNotification, 1200000);
            },
        });
    };

    /**
     * Disable main pefect scrollbar from scrolling vertically when mouse over scrollable agenda items in the case of large agenda
     */
    const handleScrollY = () => {
        if (mainScrollbarScrollTop !== null && mainScrollbarScrollTop >= 0) {
            const scrollbarContainer = document.querySelector('.scrollbar-container');
            if (scrollbarContainer)
                scrollbarContainer.scrollTop = mainScrollbarScrollTop;
        }
    }

    return (
        isLoaded
        ? (
            <ThemeSelector>
              <ErrorBoundary profile={profile}>
                <PerfectScrollbar
                   ref={psRef}
                   onScrollY={handleScrollY}
                   options={{suppressScrollX: true}}
                >
                  <Switch>
                      <Route path="/login" component={Features.Login} />
                      <Route path="/notfound" component={Features.NotFound} />
                      <Route exact path="/account/resetpassword" component={Features.PasswordReset} />
                      <Route exact path="/account/resetpassword/:token" component={Features.PasswordReset} />
                      <IdleTimer
                              ref={idleTimerRef}
                              onIdle={onIdle}
                              debounce={500}
                              timeout={TIMEOUT ? parseInt(TIMEOUT, 10) : undefined}
                      >
                          {LayoutRoutes}
                          <IdleTimeOutModal
                              isVisible={isTimeoutModalVisible}
                              setIsVisible={setIsTimeoutModalVisible}
                              idleTimerRef={idleTimerRef}
                              signOut={signOut}
                          />
                      </IdleTimer>
                  </Switch>
                  <ScrollButton />
                </PerfectScrollbar>
              </ErrorBoundary>
            </ThemeSelector>
        ) : null
    );
}

export default App;
